<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model="filter" :filters="filters" />
  </toolbar>

  <main class="px-12 overflow-hidden">
    <busy-area :busy="positionsLoading">
      <FullScreenMap v-slot="{ fullScreen }">
        <div class="map-container" :class="{ '!h-screen !w-screen': fullScreen }">
          <GMapMap
            ref="map"
            :center="center"
            :zoom="11"
            :options="defaultOptions"
            class="h-full w-full"
            map-type-id="roadmap"
          >
            <GMapMarker
              v-for="driver in filteredDriverPositions"
              :key="driver.driverId"
              :position="{ lat: driver.lat, lng: driver.lng }"
              :icon="getMarkerIcon(driver)"
              :clickable="true"
              @click="openDriverPopover(driver)"
            >
              <GMapInfoWindow
                v-if="selectedDriverId === driver.driverId"
                :key="`${driver.driverId}-${infoWindowKey}`"
                :opened="true"
                @closeclick="selectedDriverId = null"
              >
                <div class="p-2 min-w-[200px]">
                  <h4 class="font-bold text-lg">{{ driver.driverName }}</h4>
                  <div class="text-sm text-gray-600 mt-1">
                    <p>
                      {{ $t('pages.dashboard.driverLocations.lastSeen') }}:
                      {{ formatTime(driver.timestamp) }}
                    </p>
                    <p class="mt-1">
                      <span :class="driver.isActive ? 'text-green-600' : 'text-gray-500'">
                        {{
                          driver.isActive
                            ? $t('pages.dashboard.driverLocations.statusActive')
                            : $t('pages.dashboard.driverLocations.statusInactive')
                        }}
                      </span>
                    </p>
                  </div>
                  <div class="mt-3 flex gap-2">
                    <router-link
                      :to="getDriverLink(driver)"
                      class="text-blue-500 hover:underline text-sm"
                    >
                      {{ $t('pages.dashboard.driverLocations.viewDriver') }}
                    </router-link>
                    <span class="text-gray-300">|</span>
                    <router-link
                      :to="getTourLink(driver)"
                      class="text-blue-500 hover:underline text-sm"
                    >
                      {{ $t('pages.dashboard.driverLocations.viewTour') }}
                    </router-link>
                  </div>
                </div>
              </GMapInfoWindow>
            </GMapMarker>
          </GMapMap>
        </div>
      </FullScreenMap>
    </busy-area>
  </main>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, inject, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useQueryRef } from '@/hooks/url';
import Tabs from '@components/structure/navigation/Tabs.vue';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import BusyArea from '@components/utils/busy/BusyArea.vue';
import FullScreenMap from '@utils/FullScreenMap.vue';
import { useI18n } from 'vue-i18n';
import { useAsyncData, fetchApi } from '@/hooks/fetch';
import { defaultCenter, defaultOptions } from '@/utils/map';
import { format } from 'date-fns';
import type { Campaign, CampaignGroup } from '@/entities/Entities';
import { collOptionsProvider, enumOptionsProvider } from '@/components/utils/table/hooks/provider';
import type { ProviderParams } from '@/hooks/fetch/provider';
import globalFilterKey from '@utils/globalFilter/injects';

interface DriverPosition {
  driverId: string;
  driverName: string;
  lat: number;
  lng: number;
  timestamp: string;
  tourId: string;
  isActive: boolean;
}

interface Filter {
  campaign?: string;
  hasBox?: boolean | string;
  boxVersion?: string;
  boxVariant?: string;
  lastTourDateRange?: {
    start: Date | string;
    end: Date | string;
  };
}

interface Props {
  modelValue?: CampaignGroup;
}

const props = defineProps<Props>();

const { t } = useI18n();
const route = useRoute();
const globalFilter = inject(globalFilterKey, ref({})) as Ref<{ regions?: string[] }>;

const now = new Date();
const defaultStartDate = new Date(now);
defaultStartDate.setDate(defaultStartDate.getDate() - 7);

const filter = useQueryRef<Filter>('filter', {
  campaign: undefined,
  hasBox: undefined,
  boxVersion: undefined,
  boxVariant: undefined,
  lastTourDateRange: undefined,
});

const selectedDriverId = ref<string | null>(null);
const infoWindowKey = ref(0);
const center = ref(defaultCenter);

const parseDate = (value?: string | Date | null) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const ensureDefaultDateRange = () => {
  const currentRange = filter.value?.lastTourDateRange;
  if (currentRange?.start || currentRange?.end) return;

  const groupStart = parseDate(props.modelValue?.defaultStartDate as string | Date | undefined);
  const groupEnd = parseDate(props.modelValue?.defaultEndDate as string | Date | undefined);

  filter.value = {
    ...filter.value,
    lastTourDateRange: {
      start: groupStart ?? defaultStartDate,
      end: groupEnd ?? now,
    },
  };
};

watch(() => props.modelValue?.id, ensureDefaultDateRange, { immediate: true });

const campaignGroupId = computed(() => route.params.campaignGroupId as string | undefined);

const filters = [
  {
    key: 'campaign',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      searchable: true,
      minChars: 2,
      filterResults: false,
      clearOnSelect: true,
      delay: 500,
      createOption: false,
      resolveOnLoad: true,
      noOptionsText: t('inputSearchString'),
      options: (query: string, select: any) => {
        if (!campaignGroupId.value) return Promise.resolve([]);
        const filterQuery: ProviderParams = {
          filter: {
            group: campaignGroupId.value,
            ...(query ? { name: query } : {}),
            ...(!query && select.modelValue ? { id: select.modelValue } : {}),
          },
        };
        return collOptionsProvider<Campaign>('/campaigns', filterQuery, (value) => ({
          label: value.name,
          value: value.id,
        }))();
      },
      label: t('pages.dashboard.driverLocations.campaignLabel'),
    },
  },
  {
    key: 'hasBox',
    type: 'bool',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      label: t('pages.dashboard.driverLocations.hasBoxLabel'),
    },
  },
  {
    key: 'boxVersion',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      options: enumOptionsProvider('/enums/BoxType', (item: string) => ({
        label: t(`box.types.${item}`),
        value: item,
      })),
      label: t('pages.dashboard.driverLocations.boxVersionLabel'),
    },
  },
  {
    key: 'boxVariant',
    type: 'dropdown',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      options: enumOptionsProvider('/enums/BoxVariant', (item: string) => ({
        label: t(`box.variant.${item}`),
        value: item,
      })),
      label: t('pages.dashboard.driverLocations.boxVariantLabel'),
    },
  },
  {
    key: 'lastTourDateRange',
    type: 'datetime',
    group: t('pages.dashboard.filterGroups.general'),
    options: {
      label: t('pages.dashboard.driverLocations.lastTourDateRangeLabel'),
      placeholder: t('pages.dashboard.driverLocations.lastTourDateRangePlaceholder'),
    },
  },
];

const campaignId = computed(() => filter.value?.campaign);
const boxType = computed(() => filter.value?.boxVersion);
const boxVariant = computed(() => filter.value?.boxVariant);
const lastTourDateRange = computed(() => filter.value?.lastTourDateRange);
const hasBox = computed<boolean | undefined>(() => {
  const rawValue = filter.value?.hasBox;
  if (rawValue === undefined || rawValue === null || rawValue === '') return undefined;
  if (typeof rawValue === 'boolean') return rawValue;
  return rawValue === 'true';
});

const regionIds = computed<string[]>(() => {
  return globalFilter.value?.regions ?? [];
});

const queryParams = computed(() => {
  const startDate = lastTourDateRange.value?.start ? new Date(lastTourDateRange.value.start) : null;
  const endDate = lastTourDateRange.value?.end ? new Date(lastTourDateRange.value.end) : null;
  return {
    regionIds: regionIds.value,
    campaignGroupId: campaignGroupId.value,
    campaignId: campaignId.value,
    hasBox: hasBox.value,
    boxType: boxType.value,
    boxVariant: boxVariant.value,
    startDate: startDate && !Number.isNaN(startDate.getTime()) ? startDate.toISOString() : undefined,
    endDate: endDate && !Number.isNaN(endDate.getTime()) ? endDate.toISOString() : undefined,
  };
});

const { data: driverPositions, loading: positionsLoading, refetch: fetchPositions } = useAsyncData<
  DriverPosition[],
  [typeof queryParams]
>(
  async (params) => {
    const searchParams = new URLSearchParams();
    if (params.regionIds?.length) {
      params.regionIds.forEach((id) => searchParams.append('regionIds', id));
    }
    if (params.campaignGroupId) {
      searchParams.set('campaignGroupId', params.campaignGroupId);
    }
    if (params.campaignId) {
      searchParams.set('campaignId', params.campaignId);
    }
    if (params.hasBox !== undefined) {
      searchParams.set('hasBox', String(params.hasBox));
    }
    if (params.boxType) {
      searchParams.set('boxType', params.boxType);
    }
    if (params.boxVariant) {
      searchParams.set('boxVariant', params.boxVariant);
    }
    if (params.startDate) {
      searchParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      searchParams.set('endDate', params.endDate);
    }
    return fetchApi<DriverPosition[]>(`/v3/drivers/latest-positions?${searchParams.toString()}`);
  },
  [queryParams],
  { immediate: false },
);

const filteredDriverPositions = computed(() => driverPositions.value ?? []);

const getDriverLink = (driver: DriverPosition) => ({
  name: 'driver-details',
  params: {
    driverId: driver.driverId,
  },
});

const getTourLink = (driver: DriverPosition) => ({
  name: 'driver-tours',
  params: {
    driverId: driver.driverId,
  },
  query: {
    filter: encodeURIComponent(JSON.stringify({
      id: driver.tourId,
    })),
  },
});

const openDriverPopover = (driver: DriverPosition) => {
  selectedDriverId.value = driver.driverId;
  infoWindowKey.value += 1;
};

const getMarkerIcon = () => ({
  path: google.maps.SymbolPath.CIRCLE,
  fillColor: '#923136',
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 2,
  scale: 10,
});

const formatTime = (timestamp: string | Date) => {
  if (timestamp instanceof Date) {
    if (Number.isNaN(timestamp.getTime())) return '-';
    return format(timestamp, 'dd.MM.yyyy - HH:mm:ss');
  }

  const hasZone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(timestamp);
  const date = new Date(hasZone ? timestamp : `${timestamp}Z`);
  if (Number.isNaN(date.getTime())) return '-';
  return format(date, 'dd.MM.yyyy - HH:mm:ss');
};

const updateCenter = () => {
  if (filteredDriverPositions.value.length > 0) {
    const bounds = new google.maps.LatLngBounds();
    filteredDriverPositions.value.forEach((pos) => {
      bounds.extend({ lat: pos.lat, lng: pos.lng });
    });
    const centerLatLng = bounds.getCenter();
    center.value = { lat: centerLatLng.lat(), lng: centerLatLng.lng() };
  }
};

const loadPositions = async () => {
  await fetchPositions();
  updateCenter();
};

watch(queryParams, () => {
  loadPositions();
});

watch(filteredDriverPositions, () => {
  updateCenter();
});

onMounted(() => {
  loadPositions();
});
</script>

<style lang="scss" scoped>
.map-container {
  height: calc(100vh - 300px);
  overflow: hidden;
}
</style>
